import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { Activity, AlertTriangle, ArrowLeft, BarChart2, Calendar, Clock3, Filter, RefreshCw, Download, TrendingUp, Users, Wrench, Target, PackageCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './StatisticsPage.module.css';
import commonStyles from '../../styles/common.module.css';
import DateRangePicker from '../Shared/DateRangePicker';
import SearchableSelect from '../Shared/SearchableSelect';
import { ChartSpinner } from './ChartSpinner';
import { getChartColors } from './chartUtils';
import StatisticsDetailModal from './StatisticsDetailModal';
import { shouldSendToTelegram } from '../../utils/telegram';

const AGING_DETAIL_VALUES = {
  '0–7 дней': '0_7',
  '8–14 дней': '8_14',
  '15–30 дней': '15_30',
  '31–60 дней': '31_60',
  'Более 60 дней': 'over_60',
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(url);
  }, 60_000);
};

const StatisticsPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [allProjects, setAllProjects] = useState([]);
  const [serviceCenters, setServiceCenters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projectStats, setProjectStats] = useState([]);
  const [engineerStats, setEngineerStats] = useState([]);
  const [stageStats, setStageStats] = useState([]);
  const [funnelStats, setFunnelStats] = useState([]);
  const [engineerEquipmentStats, setEngineerEquipmentStats] = useState([]);
  const [serviceOverview, setServiceOverview] = useState(null);
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedServiceCenter, setSelectedServiceCenter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [projectStatsLoading, setProjectStatsLoading] = useState(false);
  const [engineerStatsLoading, setEngineerStatsLoading] = useState(false);
  const [stageStatsLoading, setStageStatsLoading] = useState(false);
  const [funnelStatsLoading, setFunnelStatsLoading] = useState(false);
  const [engineerEquipmentStatsLoading, setEngineerEquipmentStatsLoading] = useState(false);
  const [serviceOverviewLoading, setServiceOverviewLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true); // Фильтры открыты по умолчанию
  const [refreshKey, setRefreshKey] = useState(0);
  const [includeShippedStageExport, setIncludeShippedStageExport] = useState(false);
  const [exportDateMode, setExportDateMode] = useState('acceptance');
  const [detail, setDetail] = useState(null);

  const exportModeOptions = useMemo(() => ([
    { value: 'acceptance', label: 'По дате оприходования' },
    { value: 'last_change', label: 'По дате последнего изменения' },
    { value: 'repair', label: 'По дате ремонта' },
  ]), []);
  const projectOptions = useMemo(() => [
    { value: '', label: 'Выберите проект' },
    ...allProjects.map((project) => ({ value: String(project.id), label: project.name })),
  ], [allProjects]);
  const serviceCenterOptions = useMemo(() => [
    { value: '', label: 'Все сервисные центры' },
    ...serviceCenters.map((center) => ({ value: String(center.id), label: center.name })),
  ], [serviceCenters]);
  const customerOptions = useMemo(() => [
    { value: '', label: 'Выберите заказчика' },
    ...customers.map((customer) => ({
      value: String(customer.id),
      label: customer.inn ? `${customer.name} (ИНН: ${customer.inn})` : customer.name,
    })),
  ], [customers]);

  // Функция для загрузки фильтров с учетом выбранных значений
  const fetchFilters = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject) params.append('project_id', selectedProject);
      if (selectedServiceCenter) params.append('service_center_id', selectedServiceCenter);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);

      // Ограничиваем проекты только теми, что доступны пользователю
      const userProjects = user?.projects || [];
      setAllProjects(userProjects);

      const [serviceCenterRes, customerRes] = await Promise.all([
        api.get(`/core/service-centers/?${params.toString()}`),
        api.get(`/core/customers/?${params.toString()}`),
      ]);

      setServiceCenters(Array.isArray(serviceCenterRes.data) ? serviceCenterRes.data : []);
      setCustomers(Array.isArray(customerRes.data) ? customerRes.data : []);
    } catch (err) {
      console.error('Ошибка при загрузке фильтров:', err);
      toast.error('Ошибка при загрузке фильтров', {
        toastId: 'fetch-filters-error',
        autoClose: 3000,
      });
    } finally {
      setInitialLoad(false);
    }
  }, [selectedProject, selectedServiceCenter, selectedCustomer, user]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchFilters();
    }
  }, [fetchFilters, authLoading, user]);

  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedServiceCenter) params.append('service_center_id', selectedServiceCenter);
    if (selectedProject) params.append('project_id', selectedProject);
    if (selectedCustomer) params.append('customer_id', selectedCustomer);
    if (startDate && endDate && startDate <= endDate) {
      params.append('start_date', startDate.toISOString().split('T')[0]);
      params.append('end_date', endDate.toISOString().split('T')[0]);
    } else if (startDate || endDate) {
      console.warn('Некорректный диапазон дат:', { startDate, endDate });
      toast.warn('Выберите корректный диапазон дат', { toastId: 'invalid-dates', autoClose: 3000 });
    }
    return params;
  }, [selectedServiceCenter, selectedProject, selectedCustomer, startDate, endDate]);

  const trySendToTelegram = useCallback(async (url, params, successMessage, toastId) => {
    if (!shouldSendToTelegram()) return false;
    try {
      const sendParams = new URLSearchParams(params);
      sendParams.append('send_to_telegram', 'true');
      await api.get(url, { params: sendParams });
      toast.success(successMessage, { toastId, autoClose: 3000 });
      return true;
    } catch (err) {
      console.warn('Telegram send failed, fallback to download:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    const fetchStatistics = async () => {
      // Загружаем статистику только если выбран project_id или customer_id
      if (!selectedProject && !selectedCustomer) {
        setProjectStats([]);
        setEngineerStats([]);
        setStageStats([]);
        setFunnelStats([]);
        setEngineerEquipmentStats([]);
        setEquipmentModels([]);
        setServiceOverview(null);
        return;
      }

      const params = getQueryParams();
      console.debug('Запрос статистики с параметрами:', params.toString());

      setProjectStatsLoading(true);
      setEngineerStatsLoading(true);
      setStageStatsLoading(true);
      setFunnelStatsLoading(true);
      setEngineerEquipmentStatsLoading(true);
      setServiceOverviewLoading(true);
      const query = params.toString();
      const results = await Promise.allSettled([
        api.get(`/stats/project-stats/?${query}`),
        api.get(`/stats/engineer-stats/?${query}`),
        api.get(`/stats/stage-stats/?${query}`),
        api.get(`/stats/funnel-stats/?${query}`),
        api.get(`/stats/engineer-equipment-stats/?${query}`),
        api.get(`/stats/service-overview/?${query}`),
      ]);
      if (results[0].status === 'fulfilled') setProjectStats(results[0].value.data);
      if (results[1].status === 'fulfilled') setEngineerStats(results[1].value.data);
      if (results[2].status === 'fulfilled') setStageStats(results[2].value.data);
      if (results[3].status === 'fulfilled') setFunnelStats(results[3].value.data);
      if (results[4].status === 'fulfilled') {
        setEngineerEquipmentStats(results[4].value.data.stats || []);
        setEquipmentModels(results[4].value.data.models || []);
      }
      if (results[5].status === 'fulfilled') setServiceOverview(results[5].value.data);
      const failed = results.filter(result => result.status === 'rejected');
      if (failed.length) {
        console.error('Ошибки загрузки статистики:', failed);
        toast.error(`Не удалось загрузить ${failed.length} блок(а) статистики`, {
          toastId: 'fetch-statistics-error', autoClose: 3500,
        });
      }
      setProjectStatsLoading(false);
      setEngineerStatsLoading(false);
      setStageStatsLoading(false);
      setFunnelStatsLoading(false);
      setEngineerEquipmentStatsLoading(false);
      setServiceOverviewLoading(false);
    };

    if (!initialLoad) {
      fetchStatistics();
    }
  }, [getQueryParams, initialLoad, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleProjectSelect = useCallback((projectId) => {
    setSelectedProject(projectId);
    setSelectedServiceCenter(''); // Очистка зависимого фильтра
    setSelectedCustomer(''); // Очистка зависимого фильтра
  }, []);

  const handleServiceCenterSelect = useCallback((serviceCenterId) => {
    setSelectedServiceCenter(serviceCenterId);
    setSelectedCustomer(''); // Очистка зависимого фильтра
  }, []);

  const handleCustomerSelect = useCallback((customerId) => {
    setSelectedCustomer(customerId);
    setSelectedServiceCenter(''); // Очистка зависимого фильтра
  }, []);

  const handleRangeChange = useCallback((from, to) => {
    setStartDate(from);
    setEndDate(to);
  }, []);

  const openDetail = useCallback((metric, title, options = {}) => {
    setDetail({ metric, title, ...options });
  }, []);

  const detailQueryString = useMemo(() => getQueryParams().toString(), [getQueryParams]);

  const toggleFilters = () => {
    setIsFiltersOpen(prev => {
      console.log('Текущее состояние фильтров:', !prev);
      return !prev;
    });
  };

  const handleExportToExcel = async () => {
    try {
      const params = getQueryParams();
      params.append('date_mode', exportDateMode);
      if (await trySendToTelegram('/reports/export-statistics/', params, 'Файл отправлен в Telegram', 'stats-export-telegram')) {
        return;
      }

      const response = await api.get('/reports/export-statistics/', {
        params,
        responseType: 'blob',
      });

      const filename = `statistics_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadBlob(new Blob([response.data]), filename);

      toast.success('Файл успешно выгружен', { autoClose: 3000 });
    } catch (err) {
      console.error('Ошибка при выгрузке в Excel:', err);
      let message = err.response?.data?.error || 'Ошибка при выгрузке данных в Excel';
      if (err.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await err.response.data.text());
          message = payload.error || message;
        } catch {
          // Response is not JSON.
        }
      }
      toast.error(message, {
        toastId: 'export-excel-error',
        autoClose: 3000,
      });
    }
  };

  const handleStageExport = async () => {
    if (!selectedProject && !selectedCustomer) {
      toast.warn('Выберите проект или заказчика для выгрузки стадий', {
        toastId: 'stage-export-warning',
        autoClose: 3000,
      });
      return;
    }

    try {
      const params = new URLSearchParams();
      if (selectedProject) params.append('project_id', selectedProject);
      if (selectedServiceCenter) params.append('service_center_id', selectedServiceCenter);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);
      params.append('include_shipped', includeShippedStageExport ? 'true' : 'false');

      if (await trySendToTelegram('/stats/stage-stats/export/', params, 'Файл отправлен в Telegram', 'stage-export-telegram')) {
        return;
      }

      const response = await api.get('/stats/stage-stats/export/', {
        params,
        responseType: 'blob',
      });

      const filename = `stage_stats_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadBlob(new Blob([response.data]), filename);

      toast.success('Статистика по стадиям выгружена', { autoClose: 3000 });
    } catch (err) {
      console.error('Ошибка при выгрузке стадий:', err);
      toast.error('Ошибка при выгрузке стадий', {
        toastId: 'stage-export-error',
        autoClose: 3000,
      });
    }
  };

  const projectChartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Inter, system-ui, sans-serif',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        events: {
          dataPointSelection: (_event, _chart, config) => {
            const project = projectStats[config.dataPointIndex];
            if (!project) return;
            const completedSeries = config.seriesIndex === 1;
            openDetail(
              completedSeries ? 'completed_in_period' : 'project_active_period',
              `${project.project_name}: ${completedSeries ? 'завершённые ремонты' : 'оборудование в сервисе'}`,
              { projectId: project.project_id }
            );
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '50%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.9,
          },
        },
      },
      xaxis: {
        categories: projectStats.map((item) => item.project_name),
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
          },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          offsetY: 5,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
          },
        },
      },
      colors: getChartColors(0),
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#333'],
        },
        offsetY: -20,
        formatter: (val) => val,
      },
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4,
        padding: {
          bottom: 10,
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '14px',
        markers: {
          radius: 12,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontSize: '14px',
        },
        y: {
          formatter: (val) => val,
        },
      },
    }),
    [openDetail, projectStats]
  );

  const projectChartSeries = useMemo(
    () => [
      { name: 'Оборудование', data: projectStats.map((item) => item.total_equipment) },
      { name: 'Завершённые ремонты', data: projectStats.map((item) => item.completed_repairs) },
    ],
    [projectStats]
  );

  const engineerChartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Inter, system-ui, sans-serif',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
        stacked: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '25%',
        },
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.85,
          },
        },
      },
      stroke: {
        width: [0, 3, 0],
        curve: 'smooth',
      },
      xaxis: {
        categories: engineerStats.map((item) => item.engineer_name),
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
          },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          offsetY: 5,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      colors: getChartColors(1),
      dataLabels: {
        enabled: true,
        formatter: (val, { seriesIndex }) => {
          if (seriesIndex === 1) {
            return val.toFixed(2);
          }
          if (seriesIndex === 2) {
            return `${val.toFixed(1)}%`;
          }
          return val;
        },
        style: {
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#333'],
        },
        offsetY: -25,
      },
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4,
        padding: {
          left: 50,
          right: 50,
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '14px',
        markers: {
          radius: 12,
        },
        offsetY: -10,
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontSize: '14px',
        },
        y: [
          {
            formatter: (val) => val,
          },
          {
            formatter: (val) => val.toFixed(2) + ' ч',
          },
          {
            formatter: (val) => val.toFixed(1) + '%',
          },
        ],
      },
      yaxis: [
        {
          title: {
            text: 'Количество ремонтов',
            style: {
              fontSize: '13px',
              fontWeight: 500,
            },
          },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: {
              fontSize: '12px',
            },
            offsetX: -10,
          },
        },
        {
          opposite: true,
          title: {
            text: 'Среднее время (часы)',
            style: {
              fontSize: '13px',
              fontWeight: 500,
            },
          },
          min: 0,
          max: Math.max(...engineerStats.map((item) => item.average_repair_time)) * 1.5 || 1,
          forceNiceScale: true,
          labels: {
            formatter: (value) => value.toFixed(1),
            style: {
              fontSize: '12px',
            },
            offsetX: 10,
          },
        },
        {
          title: {
            text: 'Эффективность (%)',
            style: {
              fontSize: '13px',
              fontWeight: 500,
            },
          },
          min: 0,
          max: 100,
          forceNiceScale: true,
          labels: {
            formatter: (value) => `${value.toFixed(0)}%`,
            style: {
              fontSize: '12px',
            },
            offsetX: -10,
            offsetY: 0,
          },
        },
      ],
    }),
    [engineerStats]
  );

  const engineerChartSeries = useMemo(() => {
    const maxRepairs = Math.max(...engineerStats.map((item) => item.total_repairs)) || 1;
    return [
      {
        name: 'Завершённые ремонты',
        data: engineerStats.map((item) => item.total_repairs),
        type: 'column',
      },
      {
        name: 'Среднее время ремонта',
        data: engineerStats.map((item) => item.average_repair_time),
        type: 'line',
        yAxisIndex: 1,
      },
      {
        name: 'Эффективность',
        data: engineerStats.map((item) =>
          maxRepairs > 0 ? (item.total_repairs / maxRepairs) * 100 : 0
        ),
        type: 'column',
      },
    ];
  }, [engineerStats]);

  const stageChartOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        fontFamily: 'Inter, system-ui, sans-serif',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        events: {
          dataPointSelection: (_event, _chart, config) => {
            const stage = stageStats[config.dataPointIndex];
            if (stage?.key) openDetail('stage', stage.stage, { value: stage.key });
          },
        },
      },
      labels: stageStats.map((item) => item.stage),
      colors: getChartColors(2),
      stroke: {
        width: 2,
        colors: ['#fff'],
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '14px',
          fontWeight: 500,
          colors: ['#fff'],
        },
        dropShadow: {
          enabled: true,
          blur: 3,
          opacity: 0.2,
        },
        formatter: (val, opts) => {
          return opts.w.globals.series[opts.seriesIndex];
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val) => `${val}`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                offsetY: 0,
              },
              value: {
                show: true,
                fontSize: '22px',
                fontWeight: 700,
                offsetY: 8,
                formatter: (val) => val,
              },
              total: {
                show: true,
                label: 'Всего',
                fontSize: '16px',
                fontWeight: 600,
                formatter: (w) => {
                  return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                },
              },
            },
          },
        },
      },
      legend: {
        position: 'bottom',
        fontSize: '14px',
        markers: {
          radius: 12,
        },
      },
    }),
    [openDetail, stageStats]
  );

  const stageChartSeries = useMemo(() => stageStats.map((item) => item.count), [stageStats]);

  const funnelChartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        fontFamily: 'Inter, system-ui, sans-serif',
        toolbar: { show: false },
        events: {
          dataPointSelection: (_event, _chart, config) => {
            const transition = funnelStats[config.dataPointIndex];
            if (transition?.status_key) openDetail('transition', `Переходы: ${transition.status_label}`, { value: transition.status_key });
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          distributed: true,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}`,
      },
      xaxis: {
        categories: funnelStats.map((item) => item.status_label || item.status_key || 'Статус'),
        labels: {
          style: { fontSize: '13px' },
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: '13px' },
        },
      },
      colors: getChartColors(6),
      tooltip: {
        y: { formatter: (val) => `${val}` },
      },
    }),
    [funnelStats, openDetail]
  );

  const funnelChartSeries = useMemo(
    () => [{ name: 'Переходов', data: funnelStats.map((item) => item.count || 0) }],
    [funnelStats]
  );

  const headerActions = (
    <div className={styles.headerActions}>
      <button
        className={`${styles.actionButton} ${styles.filterButton} ${isFiltersOpen ? styles.active : ''}`}
        onClick={toggleFilters}
      >
        <Filter size={18} />
        <span>Фильтры</span>
        <div className={`${styles.chevron} ${isFiltersOpen ? styles.open : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.427 9.573L8 6l3.573 3.573a.5.5 0 0 0 .854-.353V4.5A.5.5 0 0 0 11.927 4H4.073a.5.5 0 0 0-.5.5v4.72a.5.5 0 0 0 .854.353z"/>
          </svg>
        </div>
      </button>

      <button className={`${styles.actionButton} ${styles.refreshButton}`} onClick={handleRefresh}>
        <RefreshCw size={18} />
        <span>Обновить</span>
      </button>

      <div className={styles.exportControls}>
        <select
          className={styles.exportSelect}
          value={exportDateMode}
          onChange={(e) => setExportDateMode(e.target.value)}
        >
          {exportModeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <button className={`${styles.actionButton} ${styles.exportButton}`} onClick={handleExportToExcel} disabled={!selectedProject && !selectedCustomer}>
          <Download size={18} />
          <span>Выгрузить в Excel</span>
        </button>
      </div>
    </div>
  );

  if (authLoading || initialLoad) {
    return (
      <div className={`${commonStyles.pageContainer} ${styles.loadingContainer}`}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка статистики...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? styles.embeddedPage : commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      {embedded ? (
        <div className={styles.embeddedToolbar}>{headerActions}</div>
      ) : (
        <div className={styles.pageHeader}>
          <button className={styles.backButton} onClick={() => navigate('/select-project')}>
            <ArrowLeft size={20} />
            <span>Назад к выбору проекта</span>
          </button>

          <div className={styles.headerContent}>
            <div className={styles.titleSection}>
              <div className={styles.titleGroup}>
                <div className={styles.iconWrapper}>
                  <BarChart2 className={styles.titleIcon} />
                </div>
                <div>
                  <h1 className={styles.pageTitle}>Статистика и отчёты</h1>
                  <p className={styles.pageSubtitle}>Аналитика производительности и отчёты по ремонтам</p>
                </div>
              </div>
            </div>

            {headerActions}
          </div>
        </div>
      )}

      <div className={`${styles.filtersContainer} ${isFiltersOpen ? styles.visible : ''}`}>
        <div className={styles.filtersContent}>
          <div className={styles.filtersGrid}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <Calendar size={16} />
                <span>Период</span>
              </label>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleRangeChange}
                className={styles.datePickerWrapper}
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <Wrench size={16} />
                <span>Сервисный центр</span>
              </label>
              <SearchableSelect
                options={serviceCenterOptions}
                value={selectedServiceCenter}
                onChange={handleServiceCenterSelect}
                placeholder="Все сервисные центры"
                ariaLabel="Сервисный центр"
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <TrendingUp size={16} />
                <span>Проект</span>
              </label>
              <SearchableSelect
                options={projectOptions}
                value={selectedProject}
                onChange={handleProjectSelect}
                placeholder="Выберите проект"
                ariaLabel="Проект"
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <Users size={16} />
                <span>Заказчик</span>
              </label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomer}
                onChange={handleCustomerSelect}
                placeholder="Выберите заказчика"
                ariaLabel="Заказчик"
              />
            </div>
          </div>
        </div>
      </div>

      {(!selectedProject && !selectedCustomer) ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <BarChart2 size={48} />
          </div>
          <h3>Выберите проект или заказчика</h3>
          <p>Для отображения статистики выберите проект или заказчика в фильтрах.</p>
        </div>
      ) : (
        <>
          <section className={styles.operationalSection}>
            <div className={styles.operationalHeader}>
              <div><h2>Текущее состояние сервиса</h2><p>Живой срез на сейчас; завершения считаются за выбранный период</p></div>
              {serviceOverviewLoading && <span>Обновление…</span>}
            </div>
            <div className={styles.operationalGrid}>
              <button type="button" onClick={() => openDetail('in_service', 'Сейчас в сервисе')}><Activity /><span>Сейчас в сервисе</span><strong>{serviceOverview?.totals?.in_service || 0}</strong></button>
              <button type="button" onClick={() => openDetail('completed_in_period', 'Завершено за выбранный период')}><Target /><span>Завершено за период</span><strong>{serviceOverview?.totals?.completed_in_period || 0}</strong></button>
              <button type="button" onClick={() => openDetail('awaiting_parts', 'Ожидают ЗИП')}><Clock3 /><span>Ожидают ЗИП</span><strong>{serviceOverview?.totals?.awaiting_parts || 0}</strong></button>
              <button type="button" onClick={() => openDetail('ready_to_ship', 'Готовы к отправке')}><PackageCheck /><span>Готовы к отправке</span><strong>{serviceOverview?.totals?.ready_to_ship || 0}</strong></button>
              <button type="button" onClick={() => openDetail('over_30_days', 'Оборудование в заявках старше 30 дней')} className={(serviceOverview?.totals?.over_30_days || 0) > 0 ? styles.riskMetric : ''}><Clock3 /><span>Старше 30 дней</span><strong>{serviceOverview?.totals?.over_30_days || 0}</strong></button>
              <button type="button" onClick={() => openDetail('basket_conflicts', 'Оборудование в корзинах со смешанными этапами')} className={(serviceOverview?.totals?.basket_conflicts || 0) > 0 ? styles.riskMetric : ''}><AlertTriangle /><span>Корзины со смешением</span><strong>{serviceOverview?.totals?.basket_conflicts || 0}</strong></button>
            </div>
            <div className={styles.operationalDetails}>
              <div className={styles.stageSnapshot}>
                <h3>Где находится оборудование</h3>
                {(serviceOverview?.by_stage || []).map(item => (
                  <button type="button" key={item.key} onClick={() => openDetail('stage', item.label, { value: item.key })}><span title={item.label}>{item.label}</span><strong>{item.count}</strong></button>
                ))}
              </div>
              <div className={styles.agingSnapshot}>
                <h3>Возраст заявок в работе</h3>
                {(serviceOverview?.aging || []).map(item => {
                  const total = serviceOverview?.totals?.in_service || 1;
                  return <button type="button" key={item.label} onClick={() => openDetail('aging', `Возраст заявок: ${item.label}`, { value: AGING_DETAIL_VALUES[item.label] })}><span>{item.label}</span><div><i style={{ width: `${Math.round(item.count / total * 100)}%` }} /></div><strong>{item.count}</strong></button>;
                })}
              </div>
            </div>
            {(serviceOverview?.projects || []).length > 1 && (
              <div className={styles.projectHealthWrap}>
                <h3>Состояние по проектам</h3>
                <table className={styles.projectHealth}><thead><tr><th>Проект</th><th>В сервисе</th><th>Ожидают ЗИП</th><th>К отправке</th><th>&gt; 30 дней</th></tr></thead>
                  <tbody>{serviceOverview.projects.map(row => <tr key={row.project_id}>
                    <td><button type="button" onClick={() => openDetail('in_service', `${row.project_name}: сейчас в сервисе`, { projectId: row.project_id })}>{row.project_name}</button></td>
                    <td><button type="button" onClick={() => openDetail('in_service', `${row.project_name}: сейчас в сервисе`, { projectId: row.project_id })}>{row.total}</button></td>
                    <td><button type="button" onClick={() => openDetail('awaiting_parts', `${row.project_name}: ожидают ЗИП`, { projectId: row.project_id })}>{row.awaiting_parts}</button></td>
                    <td><button type="button" onClick={() => openDetail('ready_to_ship', `${row.project_name}: готовы к отправке`, { projectId: row.project_id })}>{row.ready_to_ship}</button></td>
                    <td><button type="button" onClick={() => openDetail('over_30_days', `${row.project_name}: старше 30 дней`, { projectId: row.project_id })}>{row.over_30_days}</button></td>
                  </tr>)}</tbody>
                </table>
              </div>
            )}
          </section>

          <div className={styles.chartsContainer}>
            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <div className={styles.chartTitleGroup}>
                  <div className={styles.chartIconWrapper}>
                    <BarChart2 size={20} />
                  </div>
                  <h3 className={styles.chartTitle}>Статистика по проектам</h3>
                </div>
              </div>
              <div className={styles.chartBody}>
                {projectStatsLoading ? (
                  <ChartSpinner />
                ) : projectStats.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <BarChart2 size={48} />
                    </div>
                    <h3>Нет данных для отображения</h3>
                    <p>Попробуйте изменить параметры фильтрации</p>
                  </div>
                ) : (
                  <Chart
                    options={projectChartOptions}
                    series={projectChartSeries}
                    type="bar"
                    height={400}
                    className={styles.chart}
                  />
                )}
              </div>
            </div>

            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <div className={styles.chartTitleGroup}>
                  <div className={styles.chartIconWrapper}>
                    <Users size={20} />
                  </div>
                  <h3 className={styles.chartTitle}>Статистика по инженерам</h3>
                </div>
              </div>
              <div className={styles.chartBody}>
                {engineerStatsLoading ? (
                  <ChartSpinner />
                ) : engineerStats.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <Users size={48} />
                    </div>
                    <h3>Нет данных для отображения</h3>
                    <p>Попробуйте изменить параметры фильтрации</p>
                  </div>
                ) : (
                  <Chart
                    options={engineerChartOptions}
                    series={engineerChartSeries}
                    type="line"
                    height={400}
                    className={styles.chart}
                  />
                )}
              </div>
            </div>

            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <div className={styles.chartTitleGroup}>
                  <div className={styles.chartIconWrapper}>
                    <TrendingUp size={20} />
                  </div>
                  <h3 className={styles.chartTitle}>Оборудование по стадиям</h3>
                </div>
                <div className={styles.stageActions}>
                  <label className={styles.stageCheckbox}>
                    <input
                      type="checkbox"
                      checked={includeShippedStageExport}
                      onChange={(e) => setIncludeShippedStageExport(e.target.checked)}
                    />
                    Учитывать «Отправлен»
                  </label>
                  <button
                    className={`${styles.actionButton} ${styles.exportButton}`}
                    onClick={handleStageExport}
                    disabled={!selectedProject && !selectedCustomer}
                  >
                    <Download size={16} />
                    Выгрузить
                  </button>
                </div>
              </div>
              <div className={styles.chartBody}>
                {stageStatsLoading ? (
                  <ChartSpinner />
                ) : stageStats.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <TrendingUp size={48} />
                    </div>
                    <h3>Нет данных для выбранных фильтров</h3>
                    <p>Попробуйте изменить параметры фильтрации</p>
                  </div>
                ) : (
                  <Chart
                    key={`${selectedProject || 'all'}-${selectedServiceCenter || 'all'}-${selectedCustomer || 'all'}`}
                    options={stageChartOptions}
                    series={stageChartSeries}
                    type="donut"
                    height={400}
                    className={styles.chart}
                  />
                )}
              </div>
            </div>

            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <div className={styles.chartTitleGroup}>
                  <div className={styles.chartIconWrapper}>
                    <Target size={20} />
                  </div>
                  <h3 className={styles.chartTitle}>Воронка по статусам (переходы)</h3>
                </div>
              </div>
              <div className={styles.chartBody}>
                {funnelStatsLoading ? (
                  <ChartSpinner />
                ) : funnelStats.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <Target size={48} />
                    </div>
                    <h3>Нет данных для выбранных фильтров</h3>
                    <p>Попробуйте изменить параметры фильтрации</p>
                  </div>
                ) : (
                  <Chart
                    options={funnelChartOptions}
                    series={funnelChartSeries}
                    type="bar"
                    height={400}
                    className={styles.chart}
                  />
                )}
              </div>
            </div>
          </div>

          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.tableTitleGroup}>
                <div className={styles.tableIconWrapper}>
                  <Target size={20} />
                </div>
                <h3 className={styles.sectionTitle}>Статистика по инженерам и моделям оборудования</h3>
              </div>
            </div>
            <div className={styles.tableWrapper}>
              {engineerEquipmentStatsLoading ? (
                <div className={styles.tableLoading}>
                  <ChartSpinner />
                </div>
              ) : engineerEquipmentStats.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <Target size={48} />
                  </div>
                  <h3>Нет данных для отображения</h3>
                  <p>Попробуйте изменить параметры фильтрации</p>
                </div>
              ) : (
                <div className={styles.tableScrollWrapper}>
                  <table className={styles.statsTable}>
                    <thead>
                      <tr>
                        <th>Оператор</th>
                        <th>Всего</th>
                        <th>Дней</th>
                        <th>Среднее в день</th>
                        {equipmentModels.map((model) => (
                          <th key={model}>{model}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {engineerEquipmentStats.map((stat, index) => (
                        <tr key={index} className={styles.tableRow}>
                          <td className={styles.engineerName}>{stat.engineer_name}</td>
                          <td className={styles.totalValue}>{stat.total}</td>
                          <td>{stat.days}</td>
                          <td>{stat.avg_per_day}</td>
                          {equipmentModels.map((model) => (
                            <td key={model}>{stat.model_counts[model] || 0}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <StatisticsDetailModal detail={detail} queryString={detailQueryString} onClose={() => setDetail(null)} />
    </div>
  );
};

export default StatisticsPage;
